import { isPlatformBrowser, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  output,
  PLATFORM_ID,
  signal,
  viewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import {
  MediaDeviceOption,
  TelehealthCallPhase,
  TelehealthCallState,
  TelehealthConnectionQuality,
  TelehealthDisplayMode,
} from '@models';
import { ButtonComponent } from '../button/button.component';
import { SpinnerComponent } from '../spinner/spinner.component';

@Component({
  selector: 'icaredx-telehealth-call-window',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule, ButtonComponent, SpinnerComponent, NgTemplateOutlet],
  templateUrl: './telehealth-call-window.component.html',
})
export class TelehealthCallWindowComponent {
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  readonly phase = input<TelehealthCallPhase>('in-call');
  readonly state = input.required<TelehealthCallState>();
  readonly micMuted = input(false);
  readonly cameraOn = input(false);
  readonly screenSharing = input(false);
  readonly screenShareSupported = input(false);
  readonly remoteConnected = input(false);
  readonly errorMessage = input<string | null>(null);
  readonly remoteView = input<HTMLElement | null>(null);
  readonly remoteScreenView = input<HTMLElement | null>(null);
  readonly localView = input<HTMLElement | null>(null);
  readonly cameras = input<MediaDeviceOption[]>([]);
  readonly microphones = input<MediaDeviceOption[]>([]);
  readonly speakers = input<MediaDeviceOption[]>([]);
  readonly selectedCameraId = input<string | null>(null);
  readonly selectedMicrophoneId = input<string | null>(null);
  readonly selectedSpeakerId = input<string | null>(null);
  readonly endsAtUtc = input<string | null>(null);
  readonly connectedAtMs = input<number | null>(null);
  readonly connectionQuality = input<TelehealthConnectionQuality>('unknown');
  readonly displayMode = input<TelehealthDisplayMode>('side');
  readonly canExpand = input(true);
  readonly canResize = input(true);
  readonly title = input('');
  readonly scheduleLabel = input('');
  readonly timeZone = input<string | null>(null);
  readonly remoteDisplayName = input('');
  readonly localDisplayName = input('');
  readonly primaryActionLabel = input('Start Call');

  readonly toggleMute = output<void>();
  readonly toggleCamera = output<void>();
  readonly toggleScreenShare = output<void>();
  readonly hangUp = output<void>();
  readonly toggleSize = output<void>();
  readonly join = output<void>();
  readonly rejoin = output<void>();
  readonly selectCamera = output<string>();
  readonly selectMicrophone = output<string>();
  readonly selectSpeaker = output<string>();

  private readonly rootRef =
    viewChild.required<ElementRef<HTMLElement>>('root');
  private readonly previewContainer =
    viewChild<ElementRef<HTMLElement>>('previewContainer');
  private readonly remoteContainer =
    viewChild<ElementRef<HTMLElement>>('remoteContainer');
  private readonly selfContainer =
    viewChild<ElementRef<HTMLElement>>('selfContainer');

  protected readonly settingsOpen = signal(false);
  protected readonly isFullscreen = signal(false);
  private readonly nowMs = signal(0);

  protected readonly showConnecting = computed(
    () => this.state() === 'connecting'
  );
  protected readonly showReconnecting = computed(
    () => this.state() === 'reconnecting'
  );
  protected readonly showEnded = computed(
    () =>
      this.phase() === 'in-call' &&
      (this.state() === 'disconnected' || this.state() === 'error')
  );
  protected readonly showWaiting = computed(
    () => !this.remoteConnected() && this.state() === 'connected'
  );

  private readonly remoteEverConnected = signal(false);
  protected readonly waitingMessage = computed(() =>
    this.remoteEverConnected()
      ? 'The other participant left the meeting. Waiting for them to rejoin…'
      : 'Waiting for the other participant to join…'
  );
  protected readonly waitingErrored = computed(
    () =>
      this.phase() === 'waiting' &&
      (this.state() === 'error' || this.state() === 'disconnected')
  );
  protected readonly waitingErrorMessage = computed(
    () =>
      this.errorMessage() ??
      'We couldn’t connect you to the meeting. Please try again.'
  );
  protected readonly endedMessage = computed(() =>
    this.state() === 'error'
      ? (this.errorMessage() ?? 'The video call failed.')
      : 'The call has ended.'
  );
  protected readonly weakConnection = computed(
    () => this.state() === 'connected' && this.connectionQuality() === 'poor'
  );
  protected readonly endingSoon = computed(() => {
    const endsAt = this.endsAtUtc();
    if (!endsAt || this.state() !== 'connected') {
      return false;
    }
    const remaining = new Date(endsAt).getTime() - this.nowMs();
    return remaining > 0 && remaining <= 5 * 60 * 1000;
  });

  protected readonly timerTone = computed<'normal' | 'warn' | 'over'>(() => {
    const endsAt = this.endsAtUtc();
    if (!endsAt || this.state() !== 'connected') {
      return 'normal';
    }
    const remaining = new Date(endsAt).getTime() - this.nowMs();
    if (remaining <= 0) {
      return 'over';
    }
    return remaining <= 5 * 60 * 1000 ? 'warn' : 'normal';
  });

  protected readonly endsAtClasses = computed(() => {
    switch (this.timerTone()) {
      case 'warn':
        return 'text-amber-300';
      case 'over':
        return 'text-red-400';
      default:
        return 'text-white/90';
    }
  });

  protected readonly remainingLabel = computed(() => {
    const endsAt = this.endsAtUtc();
    if (!endsAt || this.state() !== 'connected') {
      return '';
    }
    const end = new Date(endsAt);
    const remaining = end.getTime() - this.nowMs();

    // Past the planned end: count up as "T+M:SS over".
    if (remaining <= 0) {
      return `T+${this.clock(-remaining)} over`;
    }

    // Within the last 5 minutes: count down as "M:SS left".
    if (remaining <= 5 * 60 * 1000) {
      return `${this.clock(remaining)} left`;
    }

    // Otherwise just show the planned end time.
    return `Ends ${end.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: this.timeZone() ?? undefined,
    })}`;
  });

  private clock(ms: number): string {
    const total = Math.floor(ms / 1000);
    const minutes = Math.floor(total / 60);
    const seconds = (total % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
  }

  protected readonly showSelfView = computed(
    () => !!this.localView() || !!this.localDisplayName()
  );
  protected readonly showSelfName = computed(
    () => !this.localView() && !!this.localDisplayName()
  );

  protected readonly selfViewClasses = computed(() => {
    const base =
      'absolute aspect-video overflow-hidden border border-white/20 bg-black shadow-lg';
    return this.displayMode() === 'mini'
      ? `${base} right-2 top-2 w-[7.8rem] rounded-md`
      : `${base} right-4 top-4 w-40 rounded-lg`;
  });

  protected readonly showRemoteName = computed(
    () =>
      this.state() === 'connected' &&
      this.remoteConnected() &&
      !this.effectiveRemoteView() &&
      !!this.remoteDisplayName()
  );

  protected readonly elapsedLabel = computed(() => {
    const start = this.connectedAtMs();
    if (start === null) {
      return '';
    }
    const total = Math.max(0, Math.floor((this.nowMs() - start) / 1000));
    const minutes = Math.floor(total / 60);
    const seconds = (total % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds} elapsed`;
  });

  protected readonly micButtonClasses = computed(() =>
    this.controlClasses(this.micMuted())
  );
  protected readonly cameraButtonClasses = computed(() =>
    this.controlClasses(!this.cameraOn())
  );
  protected readonly screenShareButtonClasses = computed(() =>
    this.controlClasses(this.screenSharing())
  );

  constructor() {
    effect(() => {
      const state = this.state();
      if (state === 'connecting' || state === 'idle') {
        this.remoteEverConnected.set(false);
      } else if (this.remoteConnected()) {
        this.remoteEverConnected.set(true);
      }
    });

    effect(() =>
      this.mount(this.remoteContainer(), this.effectiveRemoteView())
    );
    effect(() => this.mount(this.selfContainer(), this.localView()));
    effect(() => this.mount(this.previewContainer(), this.localView()));

    if (this.isBrowser) {
      this.nowMs.set(Date.now());
      const intervalId = setInterval(() => this.nowMs.set(Date.now()), 1000);
      const onFullscreenChange = () =>
        this.isFullscreen.set(!!document.fullscreenElement);
      document.addEventListener('fullscreenchange', onFullscreenChange);
      inject(DestroyRef).onDestroy(() => {
        clearInterval(intervalId);
        document.removeEventListener('fullscreenchange', onFullscreenChange);
      });
    }
  }

  private effectiveRemoteView(): HTMLElement | null {
    return this.remoteScreenView() ?? this.remoteView();
  }

  protected toggleSettings(): void {
    this.settingsOpen.update(open => !open);
  }

  protected async toggleFullscreen(): Promise<void> {
    if (!this.isBrowser) {
      return;
    }
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await this.rootRef().nativeElement.requestFullscreen();
      }
    } catch {
      // Fullscreen can be rejected by the browser; ignore.
    }
  }

  protected onCameraChange(event: Event): void {
    this.selectCamera.emit((event.target as HTMLSelectElement).value);
  }

  protected onMicrophoneChange(event: Event): void {
    this.selectMicrophone.emit((event.target as HTMLSelectElement).value);
  }

  protected onSpeakerChange(event: Event): void {
    this.selectSpeaker.emit((event.target as HTMLSelectElement).value);
  }

  private controlClasses(active: boolean): string {
    const base =
      'flex h-10 w-10 items-center justify-center rounded-full transition-colors';
    return active
      ? `${base} bg-red-600 text-white hover:bg-red-700`
      : `${base} bg-white/15 text-white hover:bg-white/25`;
  }

  private mount(
    container: ElementRef<HTMLElement> | undefined,
    element: HTMLElement | null
  ): void {
    if (!container) {
      return;
    }
    const host = container.nativeElement;
    host.replaceChildren();
    if (element) {
      host.appendChild(element);
    }
  }
}
